import { Component, EventEmitter, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Navigate } from '@ngxs/router-plugin';
import { Actions, Select, Store } from '@ngxs/store';
import { AllUserDataSource } from 'app/library/all-users/components/active-user-list/datasource';
import { AllUsersState } from 'app/library/all-users/models/all-users.state';
import { ChatUsersState } from 'app/library/all-users/models/chat-users.state';
import { AudienceRelevantExperience } from 'app/library/grow/models/audience-relevant-experience.model';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { NotifyType } from 'app/projects/shared/src/lib/enums/notify-type.enum';
import { UserRoleIdEnum } from 'app/projects/shared/src/lib/enums/user-role-id.enum';
import { User } from 'app/projects/user/src/lib/models/user';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { SearchChatUsers, SortAllUsers, SortChatUsers } from '../../../../all-users/actions/index';
import { FeatureService } from '../../../../feature/services/feature.service';

@Component({
    selector: 'app-select-group-members-dialog',
    templateUrl: './select-group-members-dialog.component.html',
    styleUrls: ['./select-group-members-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class SelectGroupMembersDialogComponent extends UnsubscribeOnDestroy implements OnInit {
    @Input()
    entitie$: Observable<User[]>;

    @Input()
    entityId: string;

    @Input()
    isNotifyCheckboxVisible = false;

    @Input()
    notifyText = 'GENERAL.MESSAGE.NOTIFY_PARTICIPANTS';

    @Input()
    showAssignAsTrainingApproverToggle = false;

    @Input()
    isRoleLabelVisible = false;

    @Input()
    title: string;

    @Input()
    dataType: string;

    @Input()
    role: string;

    @Input()
    moduleId: string;

    @Input()
    isChat: boolean;

    @Input()
    audienceRelevantExperiences: AudienceRelevantExperience[] = [];

    @Input()
    experienceIds: string[];

    @Input()
    enforceExperienceFiltering = false;
    userRole: UserRoleIdEnum;

    onConfirm = new EventEmitter<User>();

    notifyMembers = false;
    @Input()
    assignAsTrainingResponsible = false;
    dataSource: AllUserDataSource;
    displayedColumns = [];
    removable = true;
    searchInput = new FormControl();
    list: User[] = [];
    hideEmail: boolean;
    roleLabel: string;
    hasNoRelevantExperiences = false;

    @Select(AllUsersState.getTotalCount)
    totalCount$: Observable<number>;

    @Select(ChatUsersState.getTotalCount)
    totalCountChatUsers$: Observable<number>;

    @ViewChild(MatPaginator)
    paginator: MatPaginator;
    @ViewChild(MatSort)
    sort: MatSort;

    sortBy = this._store.selectSnapshot(AllUsersState.getSortBy);
    sortOrder = this._store.selectSnapshot(AllUsersState.getSortOrder);

    get isManualDataSourceMode(): boolean {
        return this.dataType === 'listingWatchers';
    }

    constructor(private _store: Store, public dialogRef: MatDialogRef<SelectGroupMembersDialogComponent>, private _action$: Actions, private _featureService: FeatureService) {
        super();

        this.hideEmail = this._featureService.checkFeatureStatus(FeatureSwitchName.ParticipantEmailVisibility, 'inactive');
    }

    async ngOnInit(): Promise<void> {
        this.hasNoRelevantExperiences = this.enforceExperienceFiltering && (!this.experienceIds || this.experienceIds.length === 0);

        if (this.hasNoRelevantExperiences) {
            this.entitie$ = of([]);
        }

        this.searchInput.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(1000), distinctUntilChanged()).subscribe((searchText) => {
            if (this.isManualDataSourceMode) {
                return;
            }

            if (this.isChat) {
                this._store.dispatch(new SearchChatUsers(searchText));
            }

            this.loadUsersPage(true);
        });

        this.dataSource = new AllUserDataSource(this.entitie$, this._store, this._action$);

        setTimeout(() => {
            if (this.hideEmail) {
                this.displayedColumns = ['avatar', 'firstName', 'lastName', 'buttons'];
            } else {
                this.displayedColumns = ['avatar', 'firstName', 'lastName', 'email', 'buttons'];
            }

            if (this.isManualDataSourceMode) {
                return;
            }

            if (this.isChat) {
                this.sortBy = this._store.selectSnapshot(ChatUsersState.getSortBy);
                this.sortOrder = this._store.selectSnapshot(ChatUsersState.getSortOrder);
            }

            if (this.sortBy && this.sortOrder) {
                this.sort.sort({
                    id: this.sortBy || '',
                    start: this.sortOrder || 'asc',
                    disableClear: false,
                });
            }

            this.loadUsersPage(false);

            this.sort.sortChange.pipe(takeUntil(this._unsubscribeAll)).subscribe((sort) => {
                if (this.isChat) {
                    this._store.dispatch(new SortChatUsers(sort));
                } else {
                    this._store.dispatch(new SortAllUsers(sort));
                }

                this.loadUsersPage(true);
            });

            this.paginator.page.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
                this.loadUsersPage(false);
            });
        });
    }

    navigateToChangeExperience(): void {
        this._store.dispatch(new Navigate([`/admin/grow/audiences/${this.entityId}/relevant-experiences`]));

        this.dialogRef.close();
    }

    removeFromList(user: User): void {
        this.list = [...this.list.filter((item) => item.id !== user.id)];

        this.dataSource = new AllUserDataSource(
            this.entitie$.pipe(
                map((users) => {
                    return users.map((item) => {
                        if (item.id === user.id) {
                            item = Object.assign(new User(), item, {
                                selected: false,
                            });
                        } else if (this.list.find((item2) => item2.id === item.id)) {
                            item = Object.assign(new User(), item, {
                                selected: true,
                            });
                        }
                        return item;
                    });
                })
            ),
            this._store,
            this._action$
        );
    }

    isUserSelectable(user: User): boolean {
        return user?.selectionStatus?.isSelectable ?? true;
    }

    getUserDisabledReasonTranslationKey(user: User): string | null {
        return user?.selectionStatus?.disabledReason ?? null;
    }

    addToList(user: User): void {
        if (!this.isUserSelectable(user)) {
            return;
        }

        this.list = [...this.list, user];
        this.dataSource = new AllUserDataSource(
            this.entitie$.pipe(
                map((users) => {
                    return users.map((item) => {
                        if (item.id === user.id || this.list.find((item2) => item2.id === item.id)) {
                            item = Object.assign(new User(), item, {
                                selected: true,
                            });
                        }
                        return item;
                    });
                })
            ),
            this._store,
            this._action$
        );
    }

    saveSelected(): void {
        this.dialogRef.close({
            list: this.list,
            notifyMembers: this.notifyMembers ? NotifyType.Notify : NotifyType.DoNotNotify,
            roleLabel: this.roleLabel,
            assignAsTrainingResponsible: this.assignAsTrainingResponsible,
        });
    }

    bookIntoCourses(): void {
        this.dialogRef.close({ list: this.list, notifyMembers: this.notifyMembers ? NotifyType.Notify : NotifyType.DoNotNotify, roleLabel: this.roleLabel, bookIntoCourses: true });
    }

    confirm(user: User): void {
        this.onConfirm.next(user);
    }

    async loadUsersPage(resetPageIndex = false, isUnassignedSelected = false): Promise<void> {
        if (this.hasNoRelevantExperiences || this.isManualDataSourceMode) {
            return;
        }

        if (resetPageIndex) {
            this.paginator.pageIndex = 0;
        }

        if (this.dataSource) {
            this.dataSource.loadUsers(
                this.dataType,
                [],
                this.entityId,
                this.searchInput.value,
                this.sort.active,
                this.sort.direction,
                this.paginator.pageSize,
                this.paginator.pageIndex + 1,
                isUnassignedSelected,
                this.role,
                false,
                this.moduleId,
                this.experienceIds && this.experienceIds.length > 0 ? this.experienceIds : [],
                [],
                [],
                [],
                false,
                false,
                false,
                [],
                this.userRole ? [this.userRole] : []
            );
        }
    }
}
